import { useRef, useState } from 'react';
import { Upload, Loader2 } from 'lucide-react';
import { Modal } from './Modal';
import { Field } from '../panel/Field';
import { useScenarioStore } from '../store/scenarioStore';
import { isCloudPersistence } from '../persistence';
import { uploadMediaAsset } from '../persistence/storage';

/**
 * Scenario settings (V6 chrome): the optional title screen, the optional end
 * screen, and branding (background colour + logo) that both screens use.
 *
 * These are scenario-level fields, not node fields, so they live in their own
 * modal rather than the per-node detail panel. Presence is the switch — turning
 * a screen "off" removes the field entirely, so the runtime falls back to its
 * default (no title/end screen). Editing autosaves through the same debounce as
 * every other mutation.
 */

// --- Local WCAG contrast helper for the brand-colour warning -------------
function channelLin(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}
function lum(hex: string): number | null {
  const h = hex.replace(/^#/, '');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  const [r, g, b] = [0, 2, 4].map((i) => channelLin(parseInt(h.slice(i, i + 2), 16)));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
function ratio(l1: number, l2: number): number {
  const hi = Math.max(l1, l2);
  const lo = Math.min(l1, l2);
  return (hi + 0.05) / (lo + 0.05);
}
/** Best achievable contrast for body text on this background (white vs near-black). */
function bestTextContrast(bgHex: string): number | null {
  const lbg = lum(bgHex);
  if (lbg == null) return null;
  const white = lum('#FFFFFF')!;
  const dark = lum('#1A1815')!;
  return Math.max(ratio(lbg, white), ratio(lbg, dark));
}
function readableFg(bgHex: string): string {
  const lbg = lum(bgHex);
  if (lbg == null) return '#FAF7F0';
  const white = lum('#FFFFFF')!;
  const dark = lum('#1A1815')!;
  return ratio(lbg, white) >= ratio(lbg, dark) ? '#FFFFFF' : '#1A1815';
}

const inputCls = 'w-full px-3 py-2 rounded border border-cream-border bg-white text-sm text-ink';
const selectCls = inputCls + ' cursor-pointer';

/**
 * Page-size presets offered in the dropdown. All 16:9. These are a UI concern
 * and deliberately live here, not in `@branchworks/shared` — the model just
 * stores whatever `{ width, height }` the author picked (or nothing, for
 * responsive). Add/adjust presets here without touching the data model.
 */
const PAGE_SIZE_PRESETS: { width: number; height: number }[] = [
  { width: 960, height: 540 },
  { width: 1024, height: 576 },
  { width: 1280, height: 720 },
  { width: 1920, height: 1080 },
];

export function SettingsModal() {
  const scenario = useScenarioStore((s) => s.scenario);
  const scenarioId = useScenarioStore((s) => s.scenarioId);
  const setOpenModal = useScenarioStore((s) => s.setOpenModal);
  const updateBranding = useScenarioStore((s) => s.updateBranding);
  const setIntroEnabled = useScenarioStore((s) => s.setIntroEnabled);
  const updateIntro = useScenarioStore((s) => s.updateIntro);
  const setClosingEnabled = useScenarioStore((s) => s.setClosingEnabled);
  const updateClosing = useScenarioStore((s) => s.updateClosing);
  const updateDisplay = useScenarioStore((s) => s.updateDisplay);

  const intro = scenario.intro;
  const closing = scenario.closing;
  const branding = scenario.branding;
  const display = scenario.display;
  const bg = branding?.backgroundColor || '';

  const canUpload = isCloudPersistence();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const onLogoPicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadError(null);
    if (!scenarioId) {
      setUploadError('Save the scenario before uploading a logo.');
      return;
    }
    setUploading(true);
    try {
      const { url } = await uploadMediaAsset(file, scenarioId, 'logo');
      updateBranding({ logoUrl: url });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const contrast = bg ? bestTextContrast(bg) : null;
  const lowContrast = contrast != null && contrast < 4.5;

  return (
    <Modal
      kicker="Branding & screens"
      title="Scenario settings"
      onClose={() => setOpenModal(null)}
      maxWidth={620}
    >
      <div className="space-y-8">
        {/* Title screen */}
        <section>
          <label className="flex items-center gap-2 text-sm font-semibold text-ink cursor-pointer">
            <input
              type="checkbox"
              checked={!!intro}
              onChange={(e) => setIntroEnabled(e.target.checked)}
            />
            Show a title screen before the scenario
          </label>
          {intro && (
            <div className="space-y-3 mt-3 pl-6">
              <Field label="Heading">
                <input
                  className={inputCls}
                  value={intro.heading}
                  onChange={(e) => updateIntro({ heading: e.target.value })}
                  placeholder="e.g. Chest pain triage"
                />
              </Field>
              <Field label="Body" hint="Optional supporting text.">
                <textarea
                  className={inputCls}
                  rows={3}
                  value={intro.body || ''}
                  onChange={(e) => updateIntro({ body: e.target.value || undefined })}
                />
              </Field>
              <Field label="Button label" hint="Defaults to “Begin”.">
                <input
                  className={inputCls}
                  value={intro.buttonLabel || ''}
                  onChange={(e) => updateIntro({ buttonLabel: e.target.value || undefined })}
                  placeholder="Begin"
                />
              </Field>
            </div>
          )}
        </section>

        {/* End screen */}
        <section>
          <label className="flex items-center gap-2 text-sm font-semibold text-ink cursor-pointer">
            <input
              type="checkbox"
              checked={!!closing}
              onChange={(e) => setClosingEnabled(e.target.checked)}
            />
            Show an end screen after an outcome
          </label>
          {closing && (
            <div className="space-y-3 mt-3 pl-6">
              <Field label="Heading">
                <input
                  className={inputCls}
                  value={closing.heading}
                  onChange={(e) => updateClosing({ heading: e.target.value })}
                  placeholder="e.g. Scenario complete"
                />
              </Field>
              <Field label="Body" hint="Optional supporting text.">
                <textarea
                  className={inputCls}
                  rows={3}
                  value={closing.body || ''}
                  onChange={(e) => updateClosing({ body: e.target.value || undefined })}
                />
              </Field>
              <Field label="Button label" hint="The exit button. Defaults to “Exit”.">
                <input
                  className={inputCls}
                  value={closing.buttonLabel || ''}
                  onChange={(e) => updateClosing({ buttonLabel: e.target.value || undefined })}
                  placeholder="Exit"
                />
              </Field>
            </div>
          )}
        </section>

        {/* Branding */}
        <section className="space-y-4">
          <div className="text-sm font-semibold text-ink">Branding</div>

          <Field label="Background color" hint="Used on the title and end screens.">
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={bg || '#1A1815'}
                onChange={(e) => updateBranding({ backgroundColor: e.target.value })}
                className="h-9 w-12 rounded border border-cream-border bg-white p-0.5 cursor-pointer"
                aria-label="Background color picker"
              />
              <input
                className={inputCls + ' max-w-[160px]'}
                value={bg}
                onChange={(e) => updateBranding({ backgroundColor: e.target.value })}
                placeholder="#15233B"
              />
              {bg && (
                <button
                  onClick={() => updateBranding({ backgroundColor: '' })}
                  className="text-xs underline text-ink-2"
                >
                  Clear
                </button>
              )}
              {bg && (
                <span
                  className="ml-auto inline-flex items-center justify-center rounded px-3 h-9 text-sm font-semibold border border-cream-border"
                  style={{ background: bg, color: readableFg(bg) }}
                  aria-hidden="true"
                >
                  Aa preview
                </span>
              )}
            </div>
            {lowContrast && (
              <div className="mt-2 text-xs text-terracotta">
                This background gives only {contrast!.toFixed(1)}:1 contrast with both white and
                dark text (WCAG AA needs 4.5:1). The runtime will still pick the more readable of
                the two, but consider a darker or lighter colour.
              </div>
            )}
          </Field>

          <Field label="Logo" hint="Shown on the title and end screens.">
            <div className="flex items-center gap-3">
              <input
                className={inputCls}
                value={branding?.logoUrl || ''}
                onChange={(e) => updateBranding({ logoUrl: e.target.value })}
                placeholder="https://…"
              />
              {canUpload && (
                <>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    onChange={onLogoPicked}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded text-sm text-white disabled:opacity-60 bg-terracotta"
                  >
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    {uploading ? 'Uploading…' : 'Upload'}
                  </button>
                </>
              )}
            </div>
            {uploadError && <div className="mt-2 text-xs text-terracotta-dark">{uploadError}</div>}
            {branding?.logoUrl && (
              <div className="mt-3 rounded border border-cream-border bg-cream-3 p-3 inline-block">
                <img
                  src={branding.logoUrl}
                  alt="Logo preview"
                  className="max-h-16 w-auto"
                  onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                />
              </div>
            )}
            {!canUpload && (
              <div className="mt-2 text-xs text-ink-3">
                Sign in with cloud storage to upload a logo, or paste an image URL above.
              </div>
            )}
          </Field>
        </section>

        {/* Output size (V7) */}
        <section className="space-y-4">
          <div className="text-sm font-semibold text-ink">Output size</div>

          <Field
            label="Page size"
            hint="A fixed 16:9 stage the runtime scales to fit, reflowing to a single column on small screens. “Responsive” fills whatever space it’s given."
          >
            <select
              className={selectCls}
              value={display?.pageSize ? `${display.pageSize.width}x${display.pageSize.height}` : ''}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) {
                  updateDisplay({ pageSize: null });
                  return;
                }
                const [width, height] = v.split('x').map(Number);
                updateDisplay({ pageSize: { width, height } });
              }}
            >
              <option value="">Responsive (fits container)</option>
              {PAGE_SIZE_PRESETS.map((p) => (
                <option key={`${p.width}x${p.height}`} value={`${p.width}x${p.height}`}>
                  {p.width} × {p.height}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Text size" hint="Base text size for scene content.">
            <select
              className={selectCls}
              value={display?.textSize ?? 'medium'}
              onChange={(e) => {
                const v = e.target.value as 'small' | 'medium' | 'large';
                // 'medium' is the runtime default, so store nothing for it —
                // keeps `display` absent when the author leaves both at default.
                updateDisplay({ textSize: v === 'medium' ? null : v });
              }}
            >
              <option value="small">Small</option>
              <option value="medium">Medium — 16px</option>
              <option value="large">Large</option>
            </select>
          </Field>
        </section>
      </div>
    </Modal>
  );
}
